import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { getStreakWatchmanOptions } from '@/src/features/watchman/services/useStreakWatchmanQuery'
import {
  getWatchmanDailyIntentionsOptions,
  useWatchmanDailyIntentionsQuery,
} from '@/src/features/watchman/services/useWatchmanDailyIntentionsQuery'
import { useToast } from '@/src/general/components/ToastContext/ToastContext'
import { parseApiError } from '@/src/general/utilities/fetcher/parseApiError'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TWatchmanDailyIntentionApi } from '../../api/types/getWatchmanIntentionsApi.types'
import { INTENTION_TYPE } from '../../models/Intention.enums'
import { useIntentionPrayMutation } from '../../services/useIntentionPrayMutation'

/**
 * IMPORTANT : For backend technical reasons, the intention status is duplicated
 * in intention.hasPrayed and also in status
 * For dailyIntentions, we only use the status
 */
export const useWatchmanPrayFlow = () => {
  const [screenIndex, setScreenIndex] = useState(0)
  const [storedIntentions, setStoredIntentions] = useState<TWatchmanDailyIntentionApi[]>([])
  const [toIntentionPrayerIds, setToIntentionPrayerIds] = useState<{ [intentionId: number]: number }>({})
  const { data: dailyIntentions, isLoading } = useWatchmanDailyIntentionsQuery()
  const { addToast } = useToast()
  const { t } = useTranslate()
  const queryClient = useQueryClient()
  const { mutateAsync } = useIntentionPrayMutation()

  const step = Math.floor(screenIndex / 2) + 1

  if (!storedIntentions?.length && dailyIntentions?.length) {
    const sortedIntentions = [...dailyIntentions].sort((a, b) => {
      if (a.status === b.status) return 0
      return a.status === 'prayed' ? -1 : 1
    })
    setStoredIntentions(sortedIntentions)
    const prayedIntentions = dailyIntentions.filter(({ status }) => status === 'prayed')
    setScreenIndex(prayedIntentions.length * 2)
  }

  const next = () => setScreenIndex((current) => current + 1)

  const pray = async () => {
    const intentionInfos = storedIntentions?.[step - 1]
    if (intentionInfos) {
      try {
        const prayer = await mutateAsync({
          id: intentionInfos.intention.id,
          type: intentionInfos.intentionType,
          landingPageSlug: undefined,
        })
        if (intentionInfos.intentionType === INTENTION_TYPE.INTENTION) {
          setToIntentionPrayerIds((prev) => ({ ...prev, [intentionInfos.intention.id]: prayer.id }))
        }
        queryClient.setQueryData(
          getWatchmanDailyIntentionsOptions({ enabled: true }).queryKey,
          (data?: TWatchmanDailyIntentionApi[]) => {
            if (!data || data.length === 0) return data
            return data.map((infos) =>
              infos.intention.id === intentionInfos.intention.id ? { ...infos, status: 'prayed' as const } : infos,
            )
          },
        )
        queryClient.invalidateQueries(getStreakWatchmanOptions())
        return prayer
      } catch (error) {
        addToast({
          message: t(parseApiError(error) || 'common.error.unknown'),
          colors: 'danger',
        })
        throw error
      }
    }
  }

  return {
    intentions: storedIntentions,
    toIntentionPrayerIds,
    isLoading,
    next,
    pray,
    screenIndex,
    step,
  }
}
